import { useState, useMemo } from 'react';
import { MessageCircle, UserPlus, X, Loader2, Search, MapPin, Trash2, Beer } from 'lucide-react';
import { useAdminUsersDetailed } from '../api/useAdminUsers';
import { useCreateGroupChat, useAddChatParticipants, useUpdateChatInfo, useCreateCloseOutChat } from '../api/useAdminChats';
import { useAdminEvents } from '../api/useAdminEvents';
import { useAdminNeighborhoods } from '../api/useAdminNeighborhoods';
import { useToast } from '@/components/ux/Toast';
import type { UserAPI } from '@shared/types/api';

interface PickerUser {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  username: string;
}

function formatUserName(u: Pick<PickerUser, 'firstName' | 'lastName'>) {
  if (u.lastName && u.lastName.length > 0) {
    return `${u.firstName} ${u.lastName.charAt(0).toUpperCase()}.`;
  }
  return u.firstName;
}

function ChatUserPicker({
  selectedUserIds,
  onChange,
  excludeUserIds = [],
  emptyHint = 'Type to search users',
}: {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  excludeUserIds?: string[];
  emptyHint?: string;
}) {
  const [search, setSearch] = useState('');
  // Cache users we've seen so selected chips don't disappear when the search changes.
  const [knownUsers, setKnownUsers] = useState<Record<string, PickerUser>>({});

  const { data: searchData, isFetching } = useAdminUsersDetailed({
    page: 1,
    limit: 50,
    search,
    sortField: 'firstName',
    sortOrder: 'asc',
    subscriptionFilter: 'all',
  });

  const excludeSet = useMemo(() => new Set(excludeUserIds), [excludeUserIds]);
  const candidateUsers = useMemo(
    () => (searchData?.data?.users ?? []).filter((u) => !excludeSet.has(u.id)) as PickerUser[],
    [searchData, excludeSet]
  );

  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);
  const selectedUsers = useMemo(
    () => selectedUserIds.map((id) => knownUsers[id]).filter(Boolean) as PickerUser[],
    [selectedUserIds, knownUsers]
  );

  const toggle = (user: PickerUser) => {
    setKnownUsers((prev) => (prev[user.id] ? prev : { ...prev, [user.id]: user }));
    if (selectedSet.has(user.id)) {
      onChange(selectedUserIds.filter((id) => id !== user.id));
    } else {
      onChange([...selectedUserIds, user.id]);
    }
  };

  const removeSelected = (userId: string) => {
    onChange(selectedUserIds.filter((id) => id !== userId));
  };

  return (
    <div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted dark:text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, username..."
          className="w-full pl-9 pr-3 py-3 bg-dark-border/50 dark:bg-neutral-700 border border-dark-border dark:border-neutral-600 rounded-lg text-sm focus:outline-none focus:border-primary-500 dark:text-neutral-100"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" />
        )}
      </div>

      {selectedUserIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => removeSelected(u.id)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-primary-500/20 text-primary-700 dark:text-primary-300 hover:bg-primary-500/30 transition-colors"
              title="Remove"
            >
              {formatUserName(u)}
              <X className="w-3 h-3" />
            </button>
          ))}
          <span className="inline-flex items-center px-2 py-1 text-xs text-dark-muted dark:text-neutral-400">
            {selectedUserIds.length} selected
          </span>
        </div>
      )}

      <div className="max-h-64 overflow-y-auto border border-dark-border dark:border-neutral-600 rounded-lg divide-y divide-dark-border dark:divide-neutral-700">
        {candidateUsers.length === 0 ? (
          <div className="px-4 py-3 text-dark-muted dark:text-neutral-400 text-sm">{emptyHint}</div>
        ) : (
          candidateUsers.slice(0, 50).map((u) => {
            const isSelected = selectedSet.has(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                  isSelected
                    ? 'bg-primary-500/20 text-primary-700 dark:text-primary-300'
                    : 'hover:bg-dark-border/50 dark:hover:bg-neutral-700/60 dark:text-neutral-100'
                }`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0 ${
                  isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'border-dark-border dark:border-neutral-500'
                }`}>
                  {isSelected ? '✓' : ''}
                </span>
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-medium">{u.firstName} {u.lastName}</span>
                  {(u.email || u.username) && (
                    <span className="text-dark-muted dark:text-neutral-400 ml-2">— {u.email || `@${u.username}`}</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ModalShell({
  icon,
  title,
  description,
  children,
  footer,
  onClose,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card border border-dark-border dark:border-neutral-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col bg-white dark:bg-neutral-800"
      >
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/20">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-center mb-2 dark:text-neutral-100">{title}</h3>
          {description && (
            <p className="text-dark-muted dark:text-neutral-400 text-center text-sm mb-6">{description}</p>
          )}
          {children}
        </div>
        <div className="p-6 border-t border-dark-border dark:border-neutral-700 flex gap-3">{footer}</div>
      </div>
    </div>
  );
}

export function CreateGroupChatModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const createMutation = useCreateGroupChat();

  const canSubmit = name.trim().length > 0 && !createMutation.isPending;

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      const res = await createMutation.mutateAsync({ name: name.trim(), userIds: selectedUserIds });
      if (!res.success || !res.data) {
        toast.error(res.error || 'Failed to create chat');
        return;
      }
      toast.success('Group chat created');
      onCreated(res.data.conversationId);
      onClose();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to create chat';
      toast.error(message);
    }
  };

  return (
    <ModalShell
      icon={<MessageCircle className="w-7 h-7 text-primary-500" />}
      title="Create group chat"
      description="A group chat is a shared room (e.g. for a neighborhood). Notifications and unread counts are disabled."
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={createMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border dark:bg-neutral-700 text-dark-muted dark:text-neutral-200 rounded-lg font-medium hover:bg-dark-border/70 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </>
      }
    >
      <div className="mb-4">
        <label className="block text-sm font-medium text-dark-muted dark:text-neutral-300 mb-1">
          Chat name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Mission Dolores · Tuesday"
          className="w-full px-4 py-3 bg-dark-border/50 dark:bg-neutral-700 border border-dark-border dark:border-neutral-600 rounded-lg text-sm focus:outline-none focus:border-primary-500 dark:text-neutral-100"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-dark-muted dark:text-neutral-300 mb-1">
          Initial members (optional)
        </label>
        <ChatUserPicker
          selectedUserIds={selectedUserIds}
          onChange={setSelectedUserIds}
        />
      </div>
    </ModalShell>
  );
}

export function AddChatParticipantsModal({
  conversationId,
  conversationTitle,
  existingParticipantIds,
  onClose,
  onAdded,
}: {
  conversationId: string;
  conversationTitle: string;
  existingParticipantIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const addMutation = useAddChatParticipants();

  const canSubmit = selectedUserIds.length > 0 && !addMutation.isPending;

  const handleAdd = async () => {
    if (!canSubmit) return;
    try {
      const res = await addMutation.mutateAsync({ conversationId, userIds: selectedUserIds });
      if (!res.success) {
        toast.error(res.error || 'Failed to add users');
        return;
      }
      toast.success(`Added ${selectedUserIds.length} user${selectedUserIds.length === 1 ? '' : 's'}`);
      onAdded();
      onClose();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to add users';
      toast.error(message);
    }
  };

  return (
    <ModalShell
      icon={<UserPlus className="w-7 h-7 text-primary-500" />}
      title="Add users to chat"
      description={conversationTitle}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={addMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border dark:bg-neutral-700 text-dark-muted dark:text-neutral-200 rounded-lg font-medium hover:bg-dark-border/70 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {addMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Adding...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" /> Add{selectedUserIds.length > 0 ? ` ${selectedUserIds.length}` : ''}
              </>
            )}
          </button>
        </>
      }
    >
      <ChatUserPicker
        selectedUserIds={selectedUserIds}
        onChange={setSelectedUserIds}
        excludeUserIds={existingParticipantIds}
        emptyHint="Type to search for users to add"
      />
    </ModalShell>
  );
}

// Convert an ISO 8601 datetime to a value usable in <input type="datetime-local">
// (local time, "YYYY-MM-DDTHH:MM"). Returns an empty string if the input is invalid.
function isoToDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ChatInfoModal({
  conversationId,
  initialTitle,
  initialChatInfo,
  onClose,
}: {
  conversationId: string;
  initialTitle: string;
  initialChatInfo: UserAPI.ChatInfoData | undefined;
  onClose: () => void;
}) {
  const toast = useToast();
  const updateMutation = useUpdateChatInfo();

  const [name, setName] = useState(initialTitle);
  const [venueName, setVenueName] = useState(initialChatInfo?.venue.name ?? '');
  const [venueAddress, setVenueAddress] = useState(initialChatInfo?.venue.address ?? '');
  const [venueMapsLink, setVenueMapsLink] = useState(initialChatInfo?.venue.googleMapsLink ?? '');
  const [meetingTimeLocal, setMeetingTimeLocal] = useState(
    isoToDatetimeLocalValue(initialChatInfo?.meetingTime)
  );
  const [description, setDescription] = useState(initialChatInfo?.description ?? '');

  const hasExisting = !!initialChatInfo;
  const venueFilled = venueName.trim().length > 0;
  const meetingFilled = meetingTimeLocal.length > 0;
  // Either provide a complete chatInfo (venue + meeting time), or none of it.
  const chatInfoValid =
    (venueFilled && meetingFilled) || (!venueFilled && !meetingFilled && description.trim().length === 0);
  const canSubmit =
    name.trim().length > 0 && chatInfoValid && !updateMutation.isPending;

  const handleSave = async () => {
    if (!canSubmit) return;
    const titleChanged = name.trim() !== initialTitle;
    const updates: { conversationId: string; title?: string; chatInfo?: UserAPI.ChatInfoData | null } = {
      conversationId,
    };
    if (titleChanged) {
      updates.title = name.trim();
    }

    if (venueFilled && meetingFilled) {
      const meetingDate = new Date(meetingTimeLocal);
      if (isNaN(meetingDate.getTime())) {
        toast.error('Invalid meeting time');
        return;
      }
      updates.chatInfo = {
        venue: {
          name: venueName.trim(),
          address: venueAddress.trim() ? venueAddress.trim() : null,
          googleMapsLink: venueMapsLink.trim() ? venueMapsLink.trim() : null,
        },
        meetingTime: meetingDate.toISOString(),
        description: description.trim() ? description.trim() : null,
      };
    } else if (hasExisting) {
      // Venue/time fields were emptied — clear chatInfo entirely.
      updates.chatInfo = null;
    }

    if (updates.title === undefined && updates.chatInfo === undefined) {
      onClose();
      return;
    }

    try {
      const res = await updateMutation.mutateAsync(updates);
      if (!res.success) {
        toast.error(res.error || 'Failed to save');
        return;
      }
      toast.success('Saved');
      onClose();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to save';
      toast.error(message);
    }
  };

  const handleClear = async () => {
    if (updateMutation.isPending) return;
    if (!window.confirm('Remove the venue, meeting time and description from this chat?')) return;
    try {
      const res = await updateMutation.mutateAsync({ conversationId, chatInfo: null });
      if (!res.success) {
        toast.error(res.error || 'Failed to clear chat info');
        return;
      }
      toast.success('Chat info cleared');
      onClose();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to clear chat info';
      toast.error(message);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-dark-border/50 dark:bg-neutral-700 border border-dark-border dark:border-neutral-600 rounded-lg text-sm focus:outline-none focus:border-primary-500 dark:text-neutral-100';
  const labelClass = 'block text-sm font-medium text-dark-muted dark:text-neutral-300 mb-1';

  return (
    <ModalShell
      icon={<MapPin className="w-7 h-7 text-primary-500" />}
      title="Chat info"
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={updateMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border dark:bg-neutral-700 text-dark-muted dark:text-neutral-200 rounded-lg font-medium hover:bg-dark-border/70 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          {hasExisting && (
            <button
              onClick={handleClear}
              disabled={updateMutation.isPending}
              className="px-4 py-3 bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              title="Remove chat info"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>
            Chat name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mission Dolores · Tuesday"
            className={inputClass}
          />
        </div>

        <div className="pt-2 border-t border-dark-border/50 dark:border-neutral-700">
          <p className="text-xs uppercase tracking-wide text-dark-muted dark:text-neutral-500 mt-3 mb-3">
            Venue & meeting
          </p>
          <p className="text-xs text-dark-muted dark:text-neutral-500 -mt-2 mb-3">
            Leave venue name and meeting time empty to omit. Both are required together.
          </p>
        </div>
        <div>
          <label className={labelClass}>Venue name</label>
          <input
            type="text"
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            placeholder="e.g. Tartine Bakery"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Address</label>
          <input
            type="text"
            value={venueAddress}
            onChange={(e) => setVenueAddress(e.target.value)}
            placeholder="600 Guerrero St, San Francisco, CA"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Google Maps link</label>
          <input
            type="url"
            value={venueMapsLink}
            onChange={(e) => setVenueMapsLink(e.target.value)}
            placeholder="https://maps.google.com/..."
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Meeting time</label>
          <input
            type="datetime-local"
            value={meetingTimeLocal}
            onChange={(e) => setMeetingTimeLocal(e.target.value)}
            className={inputClass}
          />
          <p className="text-xs text-dark-muted dark:text-neutral-500 mt-1">
            Stored as ISO 8601 (UTC) in Firestore.
          </p>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Anything else attendees should know — dress code, parking, agenda…"
            rows={3}
            className={`${inputClass} resize-y min-h-[88px]`}
          />
          <p className="text-xs text-dark-muted dark:text-neutral-500 mt-1">
            Shown at the top of the chat header expansion.
          </p>
        </div>
      </div>
    </ModalShell>
  );
}

const DEFAULT_CLOSE_OUT_DESCRIPTION =
  "Not quite ready to call it a night? We’ll be heading over to %barname% after dinner and would love for you to join us! Take all the time you need for dinner, and if you're up for keeping the good times rolling afterward, come meet us there.";

function formatEventOption(date: string, time: string, type?: string | null) {
  const d = new Date(`${date}T${time.length === 5 ? time + ':00' : time}`);
  const datePart = isNaN(d.getTime())
    ? date
    : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const timePart = isNaN(d.getTime())
    ? time
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePart} · ${timePart}${type ? ` · ${type}` : ''}`;
}

// Default the meeting time to 90 minutes after the event start, in local time.
function defaultMeetingTimeLocal(eventDate: string, eventTime: string): string {
  const time = eventTime.length === 5 ? `${eventTime}:00` : eventTime;
  const d = new Date(`${eventDate}T${time}`);
  if (isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() + 90);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CreateCloseOutChatModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const toast = useToast();
  const eventsQuery = useAdminEvents();
  const neighborhoodsQuery = useAdminNeighborhoods();
  const createMutation = useCreateCloseOutChat();

  const [eventTimeId, setEventTimeId] = useState('');
  const [neighborhoodId, setNeighborhoodId] = useState('');
  const [barRestaurantId, setBarRestaurantId] = useState('');
  const [meetingTimeLocal, setMeetingTimeLocal] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState(DEFAULT_CLOSE_OUT_DESCRIPTION);

  const events = eventsQuery.data?.data ?? [];
  const allNeighborhoods = neighborhoodsQuery.data?.data?.neighborhoods ?? [];
  const allRestaurants = neighborhoodsQuery.data?.data?.restaurants ?? [];

  // Only show events that are today or in the future, in chronological order.
  const sortedEvents = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    return [...events]
      .filter((e) => e.date >= todayStr)
      .sort((a, b) => (a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)));
  }, [events]);

  const selectedEvent = useMemo(
    () => sortedEvents.find((e) => e.id === eventTimeId) || events.find((e) => e.id === eventTimeId),
    [sortedEvents, events, eventTimeId]
  );

  // When picking an event, restrict neighborhoods to the ones tied to that
  // event (if any). Otherwise show all active neighborhoods.
  const candidateNeighborhoods = useMemo(() => {
    const ids = selectedEvent?.neighborhoodIds || null;
    const active = allNeighborhoods.filter((n) => n.isActive);
    if (ids && ids.length > 0) {
      return active.filter((n) => ids.includes(n.id));
    }
    return active;
  }, [allNeighborhoods, selectedEvent]);

  // Bars in the selected neighborhood.
  const candidateBars = useMemo(() => {
    if (!neighborhoodId) return [];
    return allRestaurants
      .filter((r) => r.neighborhoodId === neighborhoodId && r.isActive && r.type === 'bar')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allRestaurants, neighborhoodId]);

  const selectedBar = useMemo(
    () => allRestaurants.find((r) => r.id === barRestaurantId),
    [allRestaurants, barRestaurantId]
  );

  // When the event changes, reset selections that depend on it and seed a
  // sensible default meeting time.
  const handleEventChange = (id: string) => {
    setEventTimeId(id);
    setNeighborhoodId('');
    setBarRestaurantId('');
    const evt = events.find((e) => e.id === id);
    if (evt) {
      setMeetingTimeLocal(defaultMeetingTimeLocal(evt.date, evt.time));
    } else {
      setMeetingTimeLocal('');
    }
  };

  const handleNeighborhoodChange = (id: string) => {
    setNeighborhoodId(id);
    setBarRestaurantId('');
  };

  const previewDescription = description.split('%barname%').join(selectedBar?.name || '%barname%');

  const canSubmit =
    !!eventTimeId &&
    !!neighborhoodId &&
    !!barRestaurantId &&
    meetingTimeLocal.length > 0 &&
    description.trim().length > 0 &&
    !createMutation.isPending;

  const handleCreate = async () => {
    if (!canSubmit) return;
    const meetingDate = new Date(meetingTimeLocal);
    if (isNaN(meetingDate.getTime())) {
      toast.error('Invalid meeting time');
      return;
    }
    try {
      const res = await createMutation.mutateAsync({
        eventTimeId,
        neighborhoodId,
        barRestaurantId,
        meetingTime: meetingDate.toISOString(),
        name: name.trim() || undefined,
        description: description.trim() || undefined,
      });
      if (!res.success || !res.data) {
        toast.error(res.error || 'Failed to create close-out chat');
        return;
      }
      toast.success(`Close-out chat created with ${res.data.participantCount} participant${res.data.participantCount === 1 ? '' : 's'}`);
      onCreated(res.data.conversationId);
      onClose();
    } catch (err) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to create close-out chat';
      toast.error(message);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-dark-border/50 dark:bg-neutral-700 border border-dark-border dark:border-neutral-600 rounded-lg text-sm focus:outline-none focus:border-primary-500 dark:text-neutral-100';
  const labelClass = 'block text-sm font-medium text-dark-muted dark:text-neutral-300 mb-1';

  return (
    <ModalShell
      icon={<Beer className="w-7 h-7 text-primary-500" />}
      title="Create close-out chat"
      description="Invite everyone seated in a neighborhood on a given event day to meet up at a bar after dinner."
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={createMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border dark:bg-neutral-700 text-dark-muted dark:text-neutral-200 rounded-lg font-medium hover:bg-dark-border/70 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>
            Event day <span className="text-red-500">*</span>
          </label>
          <select
            value={eventTimeId}
            onChange={(e) => handleEventChange(e.target.value)}
            className={inputClass}
            disabled={eventsQuery.isLoading}
          >
            <option value="">{eventsQuery.isLoading ? 'Loading…' : 'Select an event…'}</option>
            {sortedEvents.map((e) => (
              <option key={e.id} value={e.id}>
                {formatEventOption(e.date, e.time, e.type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>
            Neighborhood <span className="text-red-500">*</span>
          </label>
          <select
            value={neighborhoodId}
            onChange={(e) => handleNeighborhoodChange(e.target.value)}
            className={inputClass}
            disabled={!eventTimeId || candidateNeighborhoods.length === 0}
          >
            <option value="">
              {!eventTimeId
                ? 'Pick an event first'
                : candidateNeighborhoods.length === 0
                ? 'No neighborhoods available'
                : 'Select a neighborhood…'}
            </option>
            {candidateNeighborhoods.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>
            Bar <span className="text-red-500">*</span>
          </label>
          <select
            value={barRestaurantId}
            onChange={(e) => setBarRestaurantId(e.target.value)}
            className={inputClass}
            disabled={!neighborhoodId}
          >
            <option value="">
              {!neighborhoodId
                ? 'Pick a neighborhood first'
                : candidateBars.length === 0
                ? 'No bars in this neighborhood'
                : 'Select a bar…'}
            </option>
            {candidateBars.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {neighborhoodId && candidateBars.length === 0 && (
            <p className="text-xs text-dark-muted dark:text-neutral-500 mt-1">
              Tip: bars are restaurants with type "bar". Add one in Neighborhoods if needed.
            </p>
          )}
        </div>

        <div>
          <label className={labelClass}>
            Meeting time <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={meetingTimeLocal}
            onChange={(e) => setMeetingTimeLocal(e.target.value)}
            className={inputClass}
          />
          <p className="text-xs text-dark-muted dark:text-neutral-500 mt-1">
            Defaults to 90 minutes after the event start.
          </p>
        </div>

        <div>
          <label className={labelClass}>Chat name (optional)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              selectedBar && selectedEvent
                ? `e.g. ${selectedBar.name} after-party`
                : 'e.g. Mission · After-party'
            }
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            className={`${inputClass} resize-y min-h-[120px]`}
          />
          <p className="text-xs text-dark-muted dark:text-neutral-500 mt-1">
            <code>%barname%</code> is replaced with the chosen bar's name.
          </p>
          {selectedBar && (
            <div className="mt-2 p-3 rounded-lg bg-dark-border/30 dark:bg-neutral-700/50 text-xs text-dark-muted dark:text-neutral-300 whitespace-pre-wrap">
              <span className="font-semibold block mb-1">Preview</span>
              {previewDescription}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
